/**
 * Login component — professional luxury-brand login experience.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
} from '@mui/icons-material';
import api from '../../services/api';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        fontFamily: 'var(--font-body)',
      }}
    >
      {/* Left panel — branding */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: '45%',
          bgcolor: '#1A1B1E',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle background pattern */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.03,
            background: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 40px,
              rgba(201,169,110,1) 40px,
              rgba(201,169,110,1) 41px
            )`,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 6 }}>
          <img
            src="/IRIS-LOGO.png"
            alt="IRIS Logo"
            style={{
              width: 200,
              height: 'auto',
              marginBottom: 32,
              filter: 'drop-shadow(0 4px 24px rgba(201,169,110,0.3))',
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              color: 'rgba(168,168,166,0.8)',
              fontFamily: 'var(--font-body)',
              lineHeight: 1.7,
              maxWidth: 320,
              mx: 'auto',
            }}
          >
            Intelligent demand forecasting and inventory optimization
            for luxury retail distribution.
          </Typography>
        </Box>

        {/* Bottom attribution */}
        <Typography
          sx={{
            position: 'absolute',
            bottom: 32,
            fontSize: '11px',
            color: 'rgba(168,168,166,0.4)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-body)',
          }}
        >
          Powered by IRIS
        </Typography>
      </Box>

      {/* Right panel — login form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'var(--color-bg-primary)',
          px: 3,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
          }}
        >
          {/* Mobile logo — shown only on small screens */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'center',
              mb: 4,
            }}
          >
            <img
              src="/IRIS-LOGO.png"
              alt="IRIS Logo"
              style={{ width: 100, height: 'auto' }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-heading)',
              mb: 0.5,
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-body)',
              mb: 4,
            }}
          >
            Sign in to your IRIS account
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 'var(--radius-md)',
                fontSize: '13px',
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                mb: 0.75,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontFamily: 'var(--font-body)',
              }}
            >
              Email
            </Typography>
            <TextField
              size="small"
              required
              fullWidth
              id="email"
              placeholder="name@company.com"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ fontSize: 18, color: 'var(--color-text-muted)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 'var(--radius-md)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '14px',
                  bgcolor: 'var(--color-surface)',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-accent)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-accent)',
                    borderWidth: '1.5px',
                  },
                },
              }}
            />

            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                mb: 0.75,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontFamily: 'var(--font-body)',
              }}
            >
              Password
            </Typography>
            <TextField
              size="small"
              required
              fullWidth
              name="password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ fontSize: 18, color: 'var(--color-text-muted)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: 'var(--color-text-muted)' }}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 18 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 'var(--radius-md)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '14px',
                  bgcolor: 'var(--color-surface)',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-accent)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-accent)',
                    borderWidth: '1.5px',
                  },
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              disableElevation
              sx={{
                height: 44,
                bgcolor: '#1A1B1E',
                color: '#FFFFFF',
                fontFamily: 'var(--font-body)',
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.02em',
                borderRadius: 'var(--radius-md)',
                textTransform: 'none',
                transition: 'all 200ms ease-out',
                '&:hover': {
                  bgcolor: '#2A2B2F',
                },
                '&.Mui-disabled': {
                  bgcolor: '#1A1B1E',
                  color: 'rgba(255,255,255,0.6)',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
              ) : (
                'Sign In'
              )}
            </Button>
          </Box>

          {/* Footer */}
          <Typography
            sx={{
              mt: 5,
              textAlign: 'center',
              fontSize: '11px',
              color: 'var(--color-text-disabled)',
              fontFamily: 'var(--font-body)',
            }}
          >
            IRIS &middot; Intelligent Retail Inventory System
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
